import React from 'react';
import { Sparkles, Brain } from 'lucide-react';

// Decorative header for the dashboard's AI section. The "neural network"
// graphic is a small inline SVG (nodes + connecting lines) rather than an
// external image — self-contained, no network dependency, and it can pick
// up the pulse animation on the active nodes.
const NEURAL_NODES: Array<[number, number]> = [
  [20, 30], [20, 70], [50, 15], [50, 50], [50, 85], [80, 30], [80, 70],
];
const NEURAL_EDGES: Array<[number, number]> = [
  [0, 2], [0, 3], [1, 3], [1, 4], [2, 5], [3, 5], [3, 6], [4, 6],
];

const AIInsightsBanner: React.FC = () => {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-fuchsia-600 shadow-lg">
      {/* Ambient glow blobs */}
      <div className="pointer-events-none absolute -top-10 -right-10 h-56 w-56 rounded-full bg-fuchsia-400 opacity-30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -left-10 h-56 w-56 rounded-full bg-cyan-400 opacity-20 blur-3xl" />

      {/* Neural network decoration */}
      <svg
        className="pointer-events-none absolute right-0 top-0 h-full w-1/2 opacity-20"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid slice"
      >
        {NEURAL_EDGES.map(([a, b], idx) => (
          <line
            key={idx}
            x1={NEURAL_NODES[a][0]}
            y1={NEURAL_NODES[a][1]}
            x2={NEURAL_NODES[b][0]}
            y2={NEURAL_NODES[b][1]}
            stroke="white"
            strokeWidth="0.5"
          />
        ))}
        {NEURAL_NODES.map(([x, y], idx) => (
          <circle
            key={idx}
            cx={x}
            cy={y}
            r={idx % 2 === 0 ? 2.5 : 1.8}
            fill="white"
            className="animate-pulse"
            style={{ animationDelay: `${idx * 200}ms`, animationDuration: '2.5s' }}
          />
        ))}
      </svg>

      <div className="relative flex items-center gap-4 px-6 py-6">
        {/* Pulsing avatar */}
        <div className="relative flex h-14 w-14 shrink-0 items-center justify-center">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/30" style={{ animationDuration: '2.5s' }} />
          <span className="absolute inline-flex h-11 w-11 rounded-full bg-white/20" />
          <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/40 backdrop-blur-sm">
            <Brain className="h-7 w-7 text-white" />
            <Sparkles className="absolute -right-1 -top-1 h-4 w-4 text-yellow-300" />
          </div>
        </div>

        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold text-white">
            AI-Powered Insights
          </h2>
          <p className="text-sm text-indigo-100">
            Pattern recognition and smart suggestions, generated live from your deployment history
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIInsightsBanner;
